import { validate } from '@cfworker/json-schema';
import { CoreExpression, Identifier } from 'jsep';
import {
  addUTCMonthsSqlStyle,
  dateDiffDays,
  getAge,
  userDateFormat
} from '../date';
import { ActivityResponseValue, ActivityType } from '../model/activity';
import { Property, PropertyType } from '../model/entity-type';
import { Expression } from '../model/expression';
import { Role } from '../model/organization';
import {
  AreaOfPersistentUnemploymentLookupItem,
  ethnicityOptions,
  genderOptions,
  maritalStatusOptions,
  primaryLanguageOptions,
  raceOptions,
  RucaLookupItem,
  sexualOrientationOptions
} from '../model/person';
import { uuidToBase58 } from '../new-id';
import { roundNumber } from '../number';
import { personName } from '../templates/person';

export type BasicProperty = Pick<
  Property,
  'property_id' | 'type' | 'choices' | 'choice_type'
>;

const _standardProperties = {
  OPERATION: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ORIGINAL_ACTIVITY_STATUS: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ORIGINAL_ACTIVITY_START_DATE: {
    property_id: 0,
    type: 'date',
    choice_type: 'none'
  },
  ORIGINAL_ACTIVITY_END_DATE: {
    property_id: 0,
    type: 'date',
    choice_type: 'none'
  },
  ORIGINAL_ACTIVITY_ASSIGNEE_USER: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ORIGINAL_ACTIVITY_ASSIGNEE_ORGANIZATION: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_ID: { property_id: 0, type: 'varchar100', choice_type: 'none' },
  ACTIVITY_DEFINITION_ID: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_STATUS: { property_id: 0, type: 'varchar100', choice_type: 'none' },
  ACTIVITY_PROJECT: { property_id: 0, type: 'varchar100', choice_type: 'none' },
  ACTIVITY_CREATE_DATE: { property_id: 0, type: 'date', choice_type: 'none' },
  ACTIVITY_START_DATE: { property_id: 0, type: 'date', choice_type: 'none' },
  ACTIVITY_END_DATE: { property_id: 0, type: 'date', choice_type: 'none' },
  ACTIVITY_CREATE_USER: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_CREATE_USER_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_CREATE_USER_FIRST_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_CREATE_USER_LAST_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_CREATE_USER_EMAIL: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_CREATE_ORGANIZATION: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_ASSIGNEE_USER: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_ASSIGNEE_USER_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_ASSIGNEE_USER_FIRST_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_ASSIGNEE_USER_LAST_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_ASSIGNEE_USER_EMAIL: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_ASSIGNEE_USER_PRIMARY_PHONE_NUMBER: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_ASSIGNEE_USER_TITLE: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_ASSIGNEE_ORGANIZATION: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_PRIMARY_PHONE_NUMBER: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_PRIMARY_EMAIL_ADDRESS: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_PRIMARY_PHYSICAL_CITY: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_PRIMARY_PHYSICAL_COUNTY: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_PRIMARY_PHYSICAL_STATE: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_PRIMARY_PHYSICAL_STREET_ADDRESS: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_PRIMARY_PHYSICAL_SUB_ADDRESS: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_PRIMARY_PHYSICAL_ZIP: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_PRIMARY_PHYSICAL_ZIP_EXT: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_CREATE_ORGANIZATION: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_IS_CLAIMED: {
    property_id: 0,
    type: 'bit',
    choice_type: 'none'
  },
  PERSON_LAST_NAME: { property_id: 0, type: 'varchar100', choice_type: 'none' },
  PERSON_FIRST_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_MIDDLE_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_NAME: { property_id: 0, type: 'varchar100', choice_type: 'none' },
  PERSON_FIRST_LAST_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_DOB: { property_id: 0, type: 'date', choice_type: 'none' },
  PERSON_SSN: { property_id: 0, type: 'varchar100', choice_type: 'none' },
  PERSON_SSN_AREA: { property_id: 0, type: 'varchar100', choice_type: 'none' },
  PERSON_SSN_GROUP: { property_id: 0, type: 'varchar100', choice_type: 'none' },
  PERSON_SSN_SERIAL: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_ID: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_VETERAN_ID: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_MEDICAID_ID: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_SCSEP_PARTICIPANT_ID: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_GENDER: {
    property_id: 0,
    type: 'int',
    choice_type: 'single',
    choices: genderOptions.map(({ choice_id, label }) => ({
      choice_id,
      score: 0,
      label,
      prompt: '',
      exclusive: false
    }))
  },
  PERSON_SEXUAL_ORIENTATION: {
    property_id: 0,
    type: 'int',
    choice_type: 'single',
    choices: sexualOrientationOptions.map(({ choice_id, label }) => ({
      choice_id,
      score: 0,
      label,
      prompt: '',
      exclusive: false
    }))
  },
  PERSON_RACE: {
    property_id: 0,
    type: 'int',
    choice_type: 'multiple',
    choices: raceOptions.map(({ choice_id, label, exclusive }) => ({
      choice_id,
      score: 0,
      label,
      prompt: '',
      exclusive
    }))
  },
  PERSON_ETHNICITY: {
    property_id: 0,
    type: 'int',
    choice_type: 'single',
    choices: ethnicityOptions.map(({ choice_id, label }) => ({
      choice_id,
      score: 0,
      label,
      prompt: '',
      exclusive: false
    }))
  },
  PERSON_MARITAL_STATUS: {
    property_id: 0,
    type: 'int',
    choice_type: 'single',
    choices: maritalStatusOptions.map(({ choice_id, label }) => ({
      choice_id,
      score: 0,
      label,
      prompt: '',
      exclusive: false
    }))
  },
  PERSON_PRIMARY_LANGUAGE: {
    property_id: 0,
    type: 'int',
    choice_type: 'single',
    choices: primaryLanguageOptions.map(({ choice_id, name: label }) => ({
      choice_id,
      score: 0,
      label,
      prompt: '',
      exclusive: false
    }))
  },
  PERSON_PRIMARY_PHONE_NUMBER: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_PHONE_AREA: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_PHONE_EXCHANGE: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_PHONE_SUBSCRIBER: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_EMAIL_ADDRESS: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_RESIDENCE_STREET_ADDRESS: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_RESIDENCE_SUB_ADDRESS: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_RESIDENCE_CITY: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_RESIDENCE_STATE: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_RESIDENCE_ZIP: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_RESIDENCE_ZIP_EXT: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_RESIDENCE_COUNTY: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  CURRENT_USER_LAST_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  CURRENT_USER_FIRST_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  CURRENT_USER_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  CURRENT_USER: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  CURRENT_USER_EMAIL: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  CURRENT_USER_ROLE: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  CURRENT_ORGANIZATION: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  }
};

export const standardProperties = _standardProperties as Record<
  keyof typeof _standardProperties,
  BasicProperty
>;

const standardPropertiesById: Record<number, BasicProperty> = {};

Object.keys(standardProperties).forEach((key, i) => {
  const property_id = i * -1 - 1;
  standardProperties[key as keyof typeof standardProperties].property_id =
    property_id;
  standardPropertiesById[property_id] = standardProperties[
    key as keyof typeof standardProperties
  ] as BasicProperty;
});

function parseExpressionDate(date: string) {
  if (!date) {
    return new Date(NaN);
  }
  switch (date.length) {
    case 23:
      return new Date(date + 'Z');
    case 10:
      return new Date(date + 'T00:00:00.000Z');
    default:
      return new Date(date);
  }
}

export interface ExpressionScopeUser {
  user_login: string;
  email: string;
  organization_login: string;
  role: Role;
  given_name: string;
  family_name: string;
}

export interface ExpressionScopeActivity {
  activity_id: string;
  activity_definition_id: string;
  parent_activity_id: string | null;
  subject_person_id: string | null;
  original_status: string;
  original_start_date: string | null;
  original_end_date: string | null;
  original_assignee_user: string | null;
  original_assignee_organization: string | null;
  status: string;
  is_claimed: boolean;
  create_date: string;
  start_date: string;
  end_date: string | null;
  project_login: string;
  create_user: string;
  create_user_first_name: string;
  create_user_last_name: string;
  create_user_email: string;
  create_organization: string;
  assignee_user: string | null;
  assignee_user_first_name: string | null;
  assignee_user_last_name: string | null;
  assignee_user_email: string | null;
  assignee_user_primary_phone_number: string | null;
  assignee_user_title: string | null;
  assignee_organization: string | null;
  primary_phone_number: string | null;
  primary_email_address: string | null;
  primary_physical_street_address: string | null;
  primary_physical_sub_address: string | null;
  primary_physical_city: string | null;
  primary_physical_state: string | null;
  primary_physical_zip: string | null;
  primary_physical_zip_ext: string | null;
  primary_physical_subregion: string | null;
}

export interface ExpressionScopePerson {
  person_id: string;
  create_organization: string;
  last_name: string;
  first_name: string;
  middle_name: string;
  suffix: string;
  nickname: string;
  dob: string | null;
  ssn: string | null;
  veteran_id: string | null;
  medicaid_id: string | null;
  medicare_beneficiary_id: string | null;
  scsep_participant_id: string | null;
  gender: number | null;
  sexual_orientation: number | null;
  race: number | null;
  ethnicity: number | null;
  marital_status: number | null;
  primary_language: number | null;
  primary_phone_number: string | null;
  primary_email_address: string | null;
  primary_residence_street_address: string | null;
  primary_residence_sub_address: string | null;
  primary_residence_city: string | null;
  primary_residence_region_abbr: string | null;
  primary_residence_postal_code: string | null;
  primary_residence_postal_code_ext: string | null;
  primary_residence_subregion: string | null;
}

export interface ActivityExpressionScope {
  activity: ExpressionScopeActivity;
  person: ExpressionScopePerson | null;
  responses: Record<string, ActivityResponseValue | null>;
  rucaLookup: RucaLookupItem[];
  areaOfPersistentUnemploymentLookup: AreaOfPersistentUnemploymentLookupItem[];
}

export interface ActivityExpressionScopeData {
  /** The current activity followed by its ancestors. */
  activityIds: string[];
  activities: Record<string, ExpressionScopeActivity>;
  people: Record<string, ExpressionScopePerson>;
  responses: Record<string, Record<string, ActivityResponseValue | null>>;
  rucaLookup: Record<string, RucaLookupItem[]>;
  areaOfPersistentUnemploymentLookup: Record<
    string,
    AreaOfPersistentUnemploymentLookupItem[]
  >;
}

export function getActivityExpressionScope(
  scopeData: ActivityExpressionScopeData,
  activityId: string | undefined
): ActivityExpressionScope | null {
  if (!activityId) {
    return null;
  }
  const activity = scopeData.activities[activityId];
  if (!activity) {
    return null;
  }
  const personId = activity.subject_person_id;
  return {
    activity,
    person: personId ? (scopeData.people[personId] ?? null) : null,
    get responses() {
      return (scopeData.responses[activityId] ??= {});
    },
    set responses(value) {
      scopeData.responses[activityId] = value;
    },
    rucaLookup: personId ? (scopeData.rucaLookup[personId] ?? []) : [],
    areaOfPersistentUnemploymentLookup: personId
      ? (scopeData.areaOfPersistentUnemploymentLookup[personId] ?? [])
      : []
  };
}

export function getCurrentActivityExpressionScope(
  scopeData: ActivityExpressionScopeData
): ActivityExpressionScope {
  const scope = getActivityExpressionScope(scopeData, scopeData.activityIds[0]);
  if (!scope) {
    throw new Error('Unable to locate current activity expression scope.');
  }
  return scope;
}

const emptyActivityExpressionScopeData = (): ActivityExpressionScopeData => ({
  activityIds: [],
  activities: {},
  people: {},
  responses: {},
  rucaLookup: {},
  areaOfPersistentUnemploymentLookup: {}
});

export class ActivityExpressionContext implements ExpressionContext {
  public hasVisiblePredecessors = false;
  private scopeIndex = 0;
  private referencedActivityId: string | null = null;
  private readonly activityScopeCache = new Map<
    string,
    ActivityExpressionScope
  >();

  static forActivity({
    currentUser,
    properties,
    scopeData,
    operation
  }: {
    currentUser: ExpressionScopeUser;
    properties: Record<
      number,
      Pick<Property, 'type' | 'choice_type' | 'choices'>
    >;
    scopeData: ActivityExpressionScopeData;
    operation: 'view' | 'create' | 'update';
  }) {
    return new ActivityExpressionContext(
      currentUser,
      {},
      properties,
      scopeData,
      operation,
      [],
      []
    );
  }

  static forPrepareDocuments({
    currentUser,
    properties,
    scopeData,
    selectedTemplates
  }: {
    currentUser: ExpressionScopeUser;
    properties: Record<
      number,
      Pick<Property, 'type' | 'choice_type' | 'choices'>
    >;
    scopeData: ActivityExpressionScopeData;
    selectedTemplates: string[];
  }) {
    return new ActivityExpressionContext(
      currentUser,
      {},
      properties,
      scopeData,
      'view',
      [],
      selectedTemplates
    );
  }

  static forDashboard({
    currentUser,
    identifierValues,
    canViewActivityTypes
  }: {
    currentUser: ExpressionScopeUser;
    identifierValues: Record<string, null | ActivityResponseValue>;
    canViewActivityTypes: ActivityType[];
  }) {
    return new ActivityExpressionContext(
      currentUser,
      identifierValues,
      {},
      emptyActivityExpressionScopeData(),
      'view',
      canViewActivityTypes,
      []
    );
  }

  private constructor(
    private readonly currentUser: ExpressionScopeUser,
    private readonly identifierValues: Record<
      string,
      null | ActivityResponseValue
    >,
    private readonly properties: Record<
      number,
      Pick<Property, 'type' | 'choice_type' | 'choices'>
    >,
    private readonly scopeData: ActivityExpressionScopeData,
    private readonly operation: 'view' | 'create' | 'update',
    private readonly canViewActivityTypes: ActivityType[],
    private readonly selectedTemplates: string[]
  ) {}

  private get currentActivityId() {
    return (
      this.referencedActivityId ?? this.scopeData.activityIds[this.scopeIndex]
    );
  }

  private getActivityScope(activityId = this.currentActivityId) {
    if (!activityId) {
      return null;
    }
    let scope = this.activityScopeCache.get(activityId);
    if (!scope) {
      scope =
        getActivityExpressionScope(this.scopeData, activityId) ?? undefined;
      if (scope) {
        this.activityScopeCache.set(activityId, scope);
      }
    }
    return scope ?? null;
  }

  private getResponseValue(property_id: number) {
    switch (property_id) {
      case standardProperties.OPERATION.property_id:
        return this.operation;
      case standardProperties.CURRENT_USER_NAME.property_id:
        return `${this.currentUser.given_name} ${this.currentUser.family_name}`.trim();
      case standardProperties.CURRENT_USER_LAST_NAME.property_id:
        return this.currentUser.family_name;
      case standardProperties.CURRENT_USER_FIRST_NAME.property_id:
        return this.currentUser.given_name;
      case standardProperties.CURRENT_USER.property_id:
        return this.currentUser.user_login;
      case standardProperties.CURRENT_USER_EMAIL.property_id:
        return this.currentUser.email;
      case standardProperties.CURRENT_USER_ROLE.property_id:
        return this.currentUser.role;
      case standardProperties.CURRENT_ORGANIZATION.property_id:
        return this.currentUser.organization_login;
    }
    const scope = this.getActivityScope();
    if (!scope) {
      return null;
    }
    const { activity, person, responses } = scope;
    if (property_id >= 0) {
      return responses[`p${property_id}`];
    }
    switch (property_id) {
      case standardProperties.ORIGINAL_ACTIVITY_STATUS.property_id:
        return activity.original_status;
      case standardProperties.ORIGINAL_ACTIVITY_START_DATE.property_id:
        return activity.original_start_date;
      case standardProperties.ORIGINAL_ACTIVITY_END_DATE.property_id:
        return activity.original_end_date;
      case standardProperties.ORIGINAL_ACTIVITY_ASSIGNEE_USER.property_id:
        return activity.original_assignee_user;
      case standardProperties.ORIGINAL_ACTIVITY_ASSIGNEE_ORGANIZATION
        .property_id:
        return activity.original_assignee_organization;
      case standardProperties.ACTIVITY_ID.property_id:
        return activity.activity_id;
      case standardProperties.ACTIVITY_DEFINITION_ID.property_id:
        return activity.activity_definition_id;
      case standardProperties.ACTIVITY_STATUS.property_id:
        return activity.status;
      case standardProperties.ACTIVITY_IS_CLAIMED.property_id:
        return activity.is_claimed;
      case standardProperties.ACTIVITY_PROJECT.property_id:
        return activity.project_login;
      case standardProperties.ACTIVITY_CREATE_DATE.property_id:
        return activity.create_date;
      case standardProperties.ACTIVITY_START_DATE.property_id:
        return activity.start_date;
      case standardProperties.ACTIVITY_END_DATE.property_id:
        return activity.end_date;
      case standardProperties.ACTIVITY_CREATE_USER.property_id:
        return activity.create_user;
      case standardProperties.ACTIVITY_CREATE_USER_NAME.property_id:
        return `${activity.create_user_first_name} ${activity.create_user_last_name}`.trim();
      case standardProperties.ACTIVITY_CREATE_USER_FIRST_NAME.property_id:
        return activity.create_user_first_name;
      case standardProperties.ACTIVITY_CREATE_USER_LAST_NAME.property_id:
        return activity.create_user_last_name;
      case standardProperties.ACTIVITY_CREATE_USER_EMAIL.property_id:
        return activity.create_user_email;
      case standardProperties.ACTIVITY_CREATE_ORGANIZATION.property_id:
        return activity.create_organization;
      case standardProperties.ACTIVITY_ASSIGNEE_USER.property_id:
        return activity.assignee_user;
      case standardProperties.ACTIVITY_ASSIGNEE_USER_NAME.property_id:
        return activity.assignee_user_last_name
          ? `${activity.assignee_user_first_name} ${activity.assignee_user_last_name}`.trim()
          : null;
      case standardProperties.ACTIVITY_ASSIGNEE_USER_FIRST_NAME.property_id:
        return activity.assignee_user_first_name;
      case standardProperties.ACTIVITY_ASSIGNEE_USER_LAST_NAME.property_id:
        return activity.assignee_user_last_name;
      case standardProperties.ACTIVITY_ASSIGNEE_USER_EMAIL.property_id:
        return activity.assignee_user_email;
      case standardProperties.ACTIVITY_ASSIGNEE_USER_PRIMARY_PHONE_NUMBER
        .property_id:
        return activity.assignee_user_primary_phone_number;
      case standardProperties.ACTIVITY_ASSIGNEE_USER_TITLE.property_id:
        return activity.assignee_user_title;
      case standardProperties.ACTIVITY_ASSIGNEE_ORGANIZATION.property_id:
        return activity.assignee_organization;
      case standardProperties.ACTIVITY_PRIMARY_PHONE_NUMBER.property_id:
        return activity.primary_phone_number;
      case standardProperties.ACTIVITY_PRIMARY_EMAIL_ADDRESS.property_id:
        return activity.primary_email_address;
      case standardProperties.ACTIVITY_PRIMARY_PHYSICAL_STREET_ADDRESS
        .property_id:
        return activity.primary_physical_street_address;
      case standardProperties.ACTIVITY_PRIMARY_PHYSICAL_SUB_ADDRESS.property_id:
        return activity.primary_physical_sub_address;
      case standardProperties.ACTIVITY_PRIMARY_PHYSICAL_CITY.property_id:
        return activity.primary_physical_city;
      case standardProperties.ACTIVITY_PRIMARY_PHYSICAL_STATE.property_id:
        return activity.primary_physical_state;
      case standardProperties.ACTIVITY_PRIMARY_PHYSICAL_ZIP.property_id:
        return activity.primary_physical_zip;
      case standardProperties.ACTIVITY_PRIMARY_PHYSICAL_ZIP_EXT.property_id:
        return activity.primary_physical_zip_ext;
      case standardProperties.ACTIVITY_PRIMARY_PHYSICAL_COUNTY.property_id:
        return activity.primary_physical_subregion;
      case standardProperties.PERSON_CREATE_ORGANIZATION.property_id:
        return person?.create_organization;
      case standardProperties.PERSON_NAME.property_id:
        return person ? personName(person) : null;
      case standardProperties.PERSON_FIRST_LAST_NAME.property_id:
        return (
          `${person?.first_name ?? ''} ${person?.last_name ?? ''}`.trim() ||
          null
        );
      case standardProperties.PERSON_LAST_NAME.property_id:
        return person?.last_name;
      case standardProperties.PERSON_FIRST_NAME.property_id:
        return person?.first_name;
      case standardProperties.PERSON_MIDDLE_NAME.property_id:
        return person?.middle_name;
      case standardProperties.PERSON_DOB.property_id:
        return person?.dob;
      case standardProperties.PERSON_SSN.property_id:
        return person?.ssn;
      case standardProperties.PERSON_SSN_AREA.property_id:
        return person?.ssn?.substr(0, 3);
      case standardProperties.PERSON_SSN_GROUP.property_id:
        return person?.ssn?.substr('xxx-'.length, 2);
      case standardProperties.PERSON_SSN_SERIAL.property_id:
        return person?.ssn?.substr('xxx-xx-'.length, 4);
      case standardProperties.PERSON_ID.property_id:
        return person?.person_id;
      case standardProperties.PERSON_VETERAN_ID.property_id:
        return person?.veteran_id;
      case standardProperties.PERSON_MEDICAID_ID.property_id:
        return person?.medicaid_id;
      case standardProperties.PERSON_SCSEP_PARTICIPANT_ID.property_id:
        return person?.scsep_participant_id;
      case standardProperties.PERSON_GENDER.property_id:
        return person?.gender;
      case standardProperties.PERSON_SEXUAL_ORIENTATION.property_id:
        return person?.sexual_orientation;
      case standardProperties.PERSON_RACE.property_id:
        return person?.race;
      case standardProperties.PERSON_ETHNICITY.property_id:
        return person?.ethnicity;
      case standardProperties.PERSON_MARITAL_STATUS.property_id:
        return person?.marital_status;
      case standardProperties.PERSON_PRIMARY_LANGUAGE.property_id:
        return person?.primary_language;
      case standardProperties.PERSON_PRIMARY_PHONE_NUMBER.property_id:
        return person?.primary_phone_number;
      case standardProperties.PERSON_PRIMARY_PHONE_AREA.property_id:
        return person?.primary_phone_number?.substr('('.length, 3);
      case standardProperties.PERSON_PRIMARY_PHONE_EXCHANGE.property_id:
        return person?.primary_phone_number?.substr('(xxx) '.length, 3);
      case standardProperties.PERSON_PRIMARY_PHONE_SUBSCRIBER.property_id:
        return person?.primary_phone_number?.substr('(xxx) xxx-'.length, 4);
      case standardProperties.PERSON_PRIMARY_EMAIL_ADDRESS.property_id:
        return person?.primary_email_address;
      case standardProperties.PERSON_PRIMARY_RESIDENCE_STREET_ADDRESS
        .property_id:
        return person?.primary_residence_street_address;
      case standardProperties.PERSON_PRIMARY_RESIDENCE_SUB_ADDRESS.property_id:
        return person?.primary_residence_sub_address;
      case standardProperties.PERSON_PRIMARY_RESIDENCE_CITY.property_id:
        return person?.primary_residence_city;
      case standardProperties.PERSON_PRIMARY_RESIDENCE_STATE.property_id:
        return person?.primary_residence_region_abbr;
      case standardProperties.PERSON_PRIMARY_RESIDENCE_ZIP.property_id:
        return person?.primary_residence_postal_code;
      case standardProperties.PERSON_PRIMARY_RESIDENCE_ZIP_EXT.property_id:
        return person?.primary_residence_postal_code_ext;
      case standardProperties.PERSON_PRIMARY_RESIDENCE_COUNTY.property_id:
        return person?.primary_residence_subregion;
      default:
        return undefined;
    }
  }

  private contextualIdentifiers: Record<string, number> = {
    DAY: 0,
    MONTH: 1,
    YEAR: 2,
    QUARTER: 3,
    WEEK: 4,
    WEEKDAY: 5
  };

  private functions: Record<string, Function> = {
    SELECTED: (property_id: number, ...codes: number[]) => {
      const response = this.getResponseValue(property_id);
      if (response === undefined || response === null) {
        return false;
      }
      if (typeof response !== 'number') {
        throw new Error(
          `Expected numeric response. ${JSON.stringify(response)}`
        );
      }
      const property =
        this.properties[property_id] ?? standardPropertiesById[property_id];
      if (!property) {
        throw new Error(
          `Expression references ${property_id} but wasn't included in scope.`
        );
      }
      if (property.choice_type === 'multiple') {
        return (codes.reduce((m, shift) => m + 2 ** shift, 0) & response) !== 0;
      } else {
        return codes.some(code => response === code);
      }
    },

    SELECTED_CHOICE_ID: (property_id: number) => {
      const response = this.getResponseValue(property_id);
      if (response === undefined || response === null) {
        return null;
      }
      if (typeof response !== 'number' || !Number.isInteger(response)) {
        throw new Error(
          `Expected integer response. ${JSON.stringify(response)}`
        );
      }
      const property =
        this.properties[property_id] ?? standardPropertiesById[property_id];
      if (!property) {
        throw new Error(
          `Expression references ${property_id} but wasn't included in scope.`
        );
      }
      if (property.choice_type !== 'single') {
        throw new Error(
          `SELECTED_CHOICE_ID can only be used with single choice properties.`
        );
      }
      return response;
    },

    VALUE: (property_id: number) => {
      const response = this.getResponseValue(property_id);
      if (response === null || response === undefined) {
        return null;
      }
      if (typeof response !== 'number' || !Number.isInteger(response)) {
        return response;
      }
      const property =
        this.properties[property_id] ?? standardPropertiesById[property_id];
      if (!property) {
        throw new Error(
          `Expression references ${property_id} but wasn't included in scope.`
        );
      }
      if (property.choice_type === 'multiple') {
        return (
          property.choices
            ?.filter(c => ((2 ** c.choice_id) & response) !== 0)
            .reduce<string | null>((sum, c) => {
              return sum ? `${sum}, ${c.label}` : c.label;
            }, null) ?? null
        );
      } else if (property.choice_type === 'single') {
        return (
          property.choices?.find(c => c.choice_id === response)?.label ?? null
        );
      } else {
        return response;
      }
    },

    ANSWERED: (property_id: number) => {
      const response = this.getResponseValue(property_id);
      return response !== null && response !== undefined && response !== '';
    },

    SCORE: (property_id: number, ...choice_ids: number[]) => {
      const response = this.getResponseValue(property_id);
      if (response === undefined || response === null) {
        return NaN;
      }
      if (typeof response !== 'number') {
        throw new Error(
          `Expected numeric response. ${JSON.stringify(response)}`
        );
      }
      const property =
        this.properties[property_id] ?? standardPropertiesById[property_id];
      if (!property) {
        throw new Error(
          `Expression references ${property_id} but wasn't included in scope.`
        );
      }
      if (property.choice_type === 'multiple') {
        return (
          property.choices
            ?.filter(
              c => choice_ids.length === 0 || choice_ids.includes(c.choice_id)
            )
            .filter(c => ((2 ** c.choice_id) & response) !== 0)
            .reduce((sum, c) => sum + c.score, 0) ?? NaN
        );
      } else if (property.choice_type === 'single') {
        return (
          property.choices
            ?.filter(
              c => choice_ids.length === 0 || choice_ids.includes(c.choice_id)
            )
            .find(c => c.choice_id === response)?.score ?? NaN
        );
      } else {
        return response;
      }
    },

    COUNTIF: (...args: boolean[]) => {
      let count = 0;
      for (const arg of args) {
        if (arg) {
          count++;
        }
      }
      return count;
    },

    ANY: (...args: any[]) => {
      return args.some(x => x);
    },

    ROUND: (value: number, digits = 0) => {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return null;
      }
      if (!Number.isInteger(digits) || digits < 0) {
        return null;
      }
      return roundNumber(value, digits);
    },

    FLOOR: (value: number) => {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return null;
      }
      return Math.floor(value);
    },

    CEILING: (value: number) => {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return null;
      }
      return Math.ceil(value);
    },

    MIN: (v1: number, v2: number) => {
      if (
        typeof v1 !== 'number' ||
        !Number.isFinite(v1) ||
        typeof v2 !== 'number' ||
        !Number.isFinite(v2)
      ) {
        return null;
      }
      return Math.min(v1, v2);
    },

    MAX: (v1: number, v2: number) => {
      if (
        typeof v1 !== 'number' ||
        !Number.isFinite(v1) ||
        typeof v2 !== 'number' ||
        !Number.isFinite(v2)
      ) {
        return null;
      }
      return Math.max(v1, v2);
    },

    ABS: (value: number) => {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return null;
      }
      return Math.abs(value);
    },

    SIGN: (value: number) => {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return null;
      }
      return value > 0 ? 1 : value < 0 ? -1 : 0;
    },

    NOW: () =>
      new Date(
        new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })
      ).toISOString(),

    AGE: (dob: string, asOf: string) => {
      const parsedDob = parseExpressionDate(dob);
      const parsedAsOf = parseExpressionDate(asOf);
      if (!isFinite(parsedDob.getTime()) || !isFinite(parsedAsOf.getTime())) {
        return null;
      }
      return getAge(parsedDob, parsedAsOf).years;
    },

    DATEADD: (part: number, value: number, date: string) => {
      const parsed = parseExpressionDate(date);
      const { DAY, MONTH, QUARTER, YEAR, WEEK } = this.contextualIdentifiers;
      if (
        ![DAY, MONTH, QUARTER, YEAR, WEEK].includes(part) ||
        typeof value !== 'number' ||
        !Number.isInteger(value) ||
        !isFinite(parsed.getTime())
      ) {
        return null;
      }
      // https://www.npmjs.com/package/dateadd?activeTab=code
      switch (part) {
        case DAY:
        case WEEK:
          if (part === WEEK) {
            value *= 7;
          }
          return new Date(
            parsed.setUTCDate(parsed.getUTCDate() + value)
          ).toISOString();
        case MONTH:
        case QUARTER:
        case YEAR:
          if (part === QUARTER) {
            value *= 3;
          } else if (part === YEAR) {
            value *= 12;
          }
          return addUTCMonthsSqlStyle(parsed, value)?.toISOString() ?? null;
        default:
          throw new Error(`Unexpected date part: ${part}`);
      }
    },

    DATEDIFF: (part: number, start: string, end: string) => {
      const parsedStart = parseExpressionDate(start);
      const parsedEnd = parseExpressionDate(end);
      const { DAY, MONTH, QUARTER, YEAR, WEEK } = this.contextualIdentifiers;
      if (
        ![DAY, MONTH, QUARTER, YEAR, WEEK].includes(part) ||
        !isFinite(parsedStart.getTime()) ||
        !isFinite(parsedEnd.getTime())
      ) {
        return null;
      }
      switch (part) {
        case DAY:
          return dateDiffDays(parsedStart, parsedEnd);
        case WEEK: {
          const weeks = Math.floor(
            (parsedEnd.getTime() - parsedStart.getTime()) / 604800000
          );
          return (
            weeks + (parsedStart.getUTCDay() > parsedEnd.getUTCDay() ? 1 : 0)
          );
        }
        case MONTH:
        case QUARTER: {
          let result =
            (parsedEnd.getUTCFullYear() - parsedStart.getUTCFullYear()) * 12 +
            parsedEnd.getUTCMonth() -
            parsedStart.getUTCMonth();
          if (part === QUARTER) {
            result = Math.floor(result / 3);
          }
          return result;
        }
        case YEAR:
          return parsedEnd.getUTCFullYear() - parsedStart.getUTCFullYear();
        default:
          throw new Error(`Unexpected date part: ${part}`);
      }
    },

    DATEPART: (part: number, date: string) => {
      const parsed = parseExpressionDate(date);
      const { DAY, MONTH, QUARTER, YEAR, WEEK, WEEKDAY } =
        this.contextualIdentifiers;
      if (
        ![DAY, MONTH, QUARTER, YEAR, WEEK, WEEKDAY].includes(part) ||
        !isFinite(parsed.getTime())
      ) {
        return null;
      }
      switch (part) {
        case DAY:
          return parsed.getUTCDate();
        case WEEK: {
          const jan1 = new Date(Date.UTC(parsed.getUTCFullYear()));
          const dayOfYear = (parsed.getTime() - jan1.getTime()) / 86400000;
          return (
            Math.floor(dayOfYear / 7) +
            1 +
            (jan1.getUTCDay() > parsed.getUTCDay() ? 1 : 0)
          );
        }
        case MONTH:
          return parsed.getUTCMonth() + 1;
        case QUARTER:
          return Math.floor(parsed.getUTCMonth() / 3) + 1;
        case YEAR:
          return parsed.getUTCFullYear();
        case WEEKDAY:
          return parsed.getUTCDay() + 1;
        default:
          throw new Error(`Unexpected date part: ${part}`);
      }
    },

    DATETRUNC: (part: number, date: string) => {
      const parsed = parseExpressionDate(date);
      const { DAY, MONTH, QUARTER, YEAR, WEEK } = this.contextualIdentifiers;
      if (
        ![DAY, MONTH, QUARTER, YEAR, WEEK].includes(part) ||
        !isFinite(parsed.getTime())
      ) {
        return null;
      }
      switch (part) {
        case DAY:
          return new Date(
            Date.UTC(
              parsed.getUTCFullYear(),
              parsed.getUTCMonth(),
              parsed.getUTCDate()
            )
          ).toISOString();
        case WEEK:
          return new Date(
            Date.UTC(
              parsed.getUTCFullYear(),
              parsed.getUTCMonth(),
              parsed.getUTCDate() - parsed.getUTCDay()
            )
          ).toISOString();
        case MONTH:
          return new Date(
            Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), 1)
          ).toISOString();
        case QUARTER:
          return new Date(
            Date.UTC(
              parsed.getUTCFullYear(),
              Math.floor(parsed.getUTCMonth() / 3) * 3,
              1
            )
          ).toISOString();
        case YEAR:
          return new Date(
            Date.UTC(parsed.getUTCFullYear(), 0, 1)
          ).toISOString();
        default:
          throw new Error(`Unexpected date part: ${part}`);
      }
    },

    EOMONTH: (date: string) => {
      const parsed = parseExpressionDate(date);
      if (!isFinite(parsed.getTime())) {
        return null;
      }
      const endOfMonth = new Date(
        Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth() + 1, 0)
      );
      return endOfMonth.toISOString();
    },

    DATE: (value: string) => {
      const parsed = parseExpressionDate(value);
      if (!isFinite(parsed.getTime())) {
        return null;
      }
      return parsed.toISOString();
    },

    IF: (condition: boolean, trueValue: unknown, falseValue: unknown) => {
      return condition ? trueValue : falseValue;
    },

    LOWER: (s: string) => {
      return s?.toLowerCase() ?? null;
    },

    UPPER: (s: string) => {
      return s?.toUpperCase() ?? null;
    },

    RURAL: (date: string) => {
      const scope = this.getActivityScope();
      if (!scope) {
        return null;
      }
      const { rucaLookup } = scope;
      const parsed = parseExpressionDate(date);
      if (!isFinite(parsed.getTime())) {
        return null;
      }
      return (
        (rucaLookup.find(
          x => parseExpressionDate(x.start_date).getTime() <= parsed.getTime()
        )?.primary_code ?? 0) >= 4
      );
    },

    AREA_OF_PERSISTENT_UNEMPLOYMENT: (date: string) => {
      const scope = this.getActivityScope();
      if (!scope) {
        return null;
      }
      const { areaOfPersistentUnemploymentLookup } = scope;
      const parsed = parseExpressionDate(date);
      if (!isFinite(parsed.getTime())) {
        return null;
      }
      return (
        areaOfPersistentUnemploymentLookup.find(
          x => parseExpressionDate(x.start_date).getTime() <= parsed.getTime()
        )?.persistent_unemployment ?? false
      );
    },

    HAS_VISIBLE_PREDECESSORS: () => {
      // console.log(`HAS_VISIBLE_PREDECESSORS: ${this.hasVisiblePredecessors}`);
      return this.hasVisiblePredecessors;
    },

    DEBUG: (label: string, value: unknown) => {
      console.log(`EXPRESSION DEBUG ${label}: ${value}`);
      return value;
    },

    CAN_VIEW_ACTIVITY_TYPE: (type: string) => {
      return this.canViewActivityTypes?.includes(type as ActivityType) ?? null;
    },

    MID(value: string, start: number, length?: number) {
      if (typeof value !== 'string') {
        return null;
      }
      if (typeof start !== 'number' || !isFinite(start)) {
        return null;
      }
      if (
        length !== undefined &&
        (typeof length !== 'number' || !isFinite(length))
      ) {
        return null;
      }
      if (length !== undefined && length <= 0) {
        return '';
      }
      start = start < 1 ? 1 : start;
      return value.substr(start - 1, length);
    },

    LEFT(value: string, length: number) {
      if (typeof value !== 'string') {
        return null;
      }
      if (typeof length !== 'number' || !isFinite(length)) {
        return null;
      }
      if (length <= 0) {
        return '';
      }
      return value.substr(0, length);
    },

    RIGHT(value: string, length: number) {
      if (typeof value !== 'string') {
        return null;
      }
      if (typeof length !== 'number' || !isFinite(length)) {
        return null;
      }
      if (length <= 0) {
        return '';
      }
      return value.slice(-1 * length);
    },

    MATCH<T>(value: T, ...args: T[]) {
      if (
        value === null ||
        value === undefined ||
        (typeof value !== 'string' && typeof value !== 'number') ||
        (typeof value === 'number' && !isFinite(value))
      ) {
        return null;
      }
      return args.includes(value);
    },

    SWITCH(value: unknown, ...args: unknown[]) {
      if (value === null || value === undefined) {
        return null;
      }
      for (let i = 0; i < args.length; i += 2) {
        if (args[i] === value) {
          return args[i + 1];
        }
      }
      return args.length % 2 === 0 ? null : args[args.length - 1];
    },

    IFS(...args: unknown[]) {
      const hasElse = args.length % 2 !== 0;
      const pairLength = hasElse ? args.length - 1 : args.length;
      for (let i = 0; i < pairLength; i += 2) {
        if (args[i]) {
          return args[i + 1];
        }
      }
      return hasElse ? args[args.length - 1] : null;
    },

    TEXT(value: unknown) {
      if (value === null || value === undefined) {
        return null;
      }
      return String(value);
    },

    FORMAT_DATE(date: string, format: unknown) {
      const parsed = parseExpressionDate(date);
      if (!isFinite(parsed.getTime())) {
        return null;
      }
      // not handling time zones!!!
      switch (format) {
        case 'yyyy-MM-dd':
          return new Date(parsed).toISOString().substr(0, 10);
        case 'M/d/yyyy':
          return userDateFormat.format(new Date(parsed));
        default:
          return null;
      }
    },

    URL_ENCODE(value: unknown) {
      if (typeof value !== 'string') {
        return null;
      }
      return encodeURIComponent(value);
    },

    URL_PARAMS(...values: unknown[]) {
      const p = new URLSearchParams();
      let k, v;
      for (let i = 0; i < values.length; i += 2) {
        k = values[i];
        v = values[i + 1];
        if (
          typeof k === 'string' &&
          k.length > 0 &&
          typeof v === 'string' &&
          v.length > 0
        ) {
          p.append(k, v);
        }
      }
      return p.toString();
    },

    UUID_TO_BASE58(value: unknown) {
      if (typeof value !== 'string') {
        return null;
      }
      if (!validate(value, { type: 'string', format: 'uuid' }).valid) {
        return null;
      }
      return uuidToBase58(value);
    },

    COALESCE(value1: unknown, value2: unknown) {
      return value1 ?? value2 ?? null;
    },

    SIGNATURE(_value: unknown) {
      return null;
    },

    SIGNATURE_DATE(_value: unknown) {
      return null;
    },

    SELECTED_TEMPLATES: (separator: unknown) => {
      if (typeof separator !== 'string') {
        return null;
      }
      return this.selectedTemplates.join(separator);
    }
  };

  resolveFunction(name: string): Function | undefined {
    if (Object.hasOwn(this.functions, name)) {
      return this.functions[name];
    }
    return undefined;
  }

  resolveIdentifier(name: string): any {
    if (Object.hasOwn(standardProperties, name)) {
      return standardProperties[name as keyof typeof standardProperties]
        .property_id;
    }
    if (Object.hasOwn(this.contextualIdentifiers, name)) {
      return this.contextualIdentifiers[name];
    }
    if (Object.hasOwn(this.identifierValues, name)) {
      return this.identifierValues[name];
    }
    return null;
  }

  ascendScope(levels: number) {
    const original = this.scopeIndex;
    this.scopeIndex += levels;
    return () => {
      this.scopeIndex = original;
    };
  }

  enterReferencedScope(propertyId: number) {
    const response = this.getResponseValue(propertyId);
    if (typeof response !== 'string') {
      return null;
    }
    const referencedActivityId = response.toLowerCase();
    if (!this.getActivityScope(referencedActivityId)) {
      return null;
    }
    const original = this.referencedActivityId;
    this.referencedActivityId = referencedActivityId;
    return () => {
      this.referencedActivityId = original;
    };
  }
}

// note: it may not be necessary to coerce values as is done here.
export function evaluateActivityExpression(
  propertyType: PropertyType,
  { type, ast }: Expression,
  context: ActivityExpressionContext
): ActivityResponseValue | null {
  const result = evaluateExpression(ast, context);
  if (result === null) {
    return null;
  }
  switch (type) {
    case 'boolean':
      // int used because there's no boolean/bit response table.
      return Boolean(result) ? 1 : 0;
    case 'int': {
      const value = Math.floor(Number(result));
      if (!isFinite(value)) {
        return null;
      }
      return value;
    }
    // @ts-expect-error
    case 'currency': // todo: Fix currency type expressions in db, if any. Should be decimal.
    case 'decimal': {
      const value = Number(result);
      if (!isFinite(value)) {
        return null;
      }
      return propertyType === 'currency' ? roundNumber(value, 2) : value;
    }
    case 'string':
      return String(result);
    case 'date':
    case 'datetime': {
      const value = parseExpressionDate(result);
      if (!isFinite(value.getTime())) {
        return null;
      }
      return value.toISOString();
    }
    default:
      throw new Error(`unsupported expression type "${type}"`);
  }
}

const binops: Record<string, { (a: any, b: any): any }> = {
  '||': function (a, b) {
    return a || b;
  },
  '&&': function (a, b) {
    return a && b;
  },
  // '|': function (a, b) {
  //   return a | b;
  // },
  // '^': function (a, b) {
  //   return a ^ b;
  // },
  // '&': function (a, b) {
  //   return a & b;
  // },
  '==': function (a, b) {
    return a == b;
  },
  '!=': function (a, b) {
    return a != b;
  },
  // '===': function (a, b) {
  //   return a === b;
  // },
  // '!==': function (a, b) {
  //   return a !== b;
  // },
  '<': function (a, b) {
    return a < b;
  },
  '>': function (a, b) {
    return a > b;
  },
  '<=': function (a, b) {
    return a <= b;
  },
  '>=': function (a, b) {
    return a >= b;
  },
  // '<<': function (a, b) {
  //   return a << b;
  // },
  // '>>': function (a, b) {
  //   return a >> b;
  // },
  // '>>>': function (a, b) {
  //   return a >>> b;
  // },
  '+': function (a, b) {
    return a + b;
  },
  '-': function (a, b) {
    return a - b;
  },
  '*': function (a, b) {
    return a * b;
  },
  '/': function (a, b) {
    return a / b;
  },
  '%': function (a, b) {
    return a % b;
  }
};

const unops: Record<string, { (a: any): any }> = {
  '-': function (a) {
    return -a;
  },
  // '+': function (a) {
  //   return +a;
  // },
  // '~': function (a) {
  //   return ~a;
  // },
  '!': function (a) {
    return !a;
  }
};

export interface ExpressionContext {
  resolveIdentifier: (name: string) => any;
  resolveFunction: (name: string) => Function | undefined;
  ascendScope: (levels: number) => () => void;
  enterReferencedScope: (propertyId: number) => (() => void) | null;
}

export function evaluateExpression(
  node: CoreExpression,
  context: ExpressionContext
): any {
  switch (node.type) {
    // case 'ArrayExpression':
    //   return evaluateArray(node.elements, context);

    case 'BinaryExpression': {
      const left = evaluateExpression(node.left as CoreExpression, context);
      if (node.operator === '||') {
        return (
          left || evaluateExpression(node.right as CoreExpression, context)
        );
      } else if (node.operator === '&&') {
        return (
          left && evaluateExpression(node.right as CoreExpression, context)
        );
      }
      if (typeof left === 'number' && !isFinite(left)) {
        return null;
      }
      const right = evaluateExpression(node.right as CoreExpression, context);
      if (typeof right === 'number' && !isFinite(right)) {
        return null;
      }
      if (
        (left === null || right === null) &&
        !['==', '!='].includes(node.operator)
      ) {
        // don't allow null coercion for non-equality operators.
        return null;
      }
      {
        // hack: ensure string comparisons of date strings use equal length strings. Need to use Date instances internally.
        const dateString = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}\.\d+Z)?$/i;
        if (
          typeof left === 'string' &&
          typeof right === 'string' &&
          left.length !== right.length &&
          dateString.test(left) &&
          dateString.test(right)
        ) {
          return binops[node.operator](
            (left + 'T00:00:00.000Z').substr(0, 24),
            (right + 'T00:00:00.000Z').substr(0, 24)
          );
        }
      }
      return binops[node.operator](left, right);
    }
    case 'CallExpression': {
      const callee = node.callee as Identifier;
      if (callee.type !== 'Identifier') {
        return undefined;
      }
      const name = callee.name.toUpperCase();
      if (name === 'ANCESTOR') {
        const levels = (node.arguments[1]?.value as number) ?? 1;
        const restoreScope = context.ascendScope(levels);
        try {
          return evaluateExpression(
            node.arguments[0] as CoreExpression,
            context
          );
        } finally {
          restoreScope();
        }
      }
      if (name === 'REFERENCED') {
        const propertyId = evaluateExpression(
          node.arguments[0] as CoreExpression,
          context
        );
        if (typeof propertyId !== 'number' || !Number.isInteger(propertyId)) {
          return null;
        }
        const restoreScope = context.enterReferencedScope(propertyId);
        if (!restoreScope) {
          return null;
        }
        try {
          return evaluateExpression(
            node.arguments[1] as CoreExpression,
            context
          );
        } finally {
          restoreScope();
        }
      }
      const fn = context.resolveFunction(name);
      if (typeof fn !== 'function') {
        return undefined;
      }
      return fn.apply(
        null,
        node.arguments.map(node =>
          evaluateExpression(node as CoreExpression, context)
        )
      );
      // // Coerce callee name to uppercase.
      // callee.name = callee.name.toUpperCase();
      // const fn = evaluateExpression(callee, scope);
      // if (typeof fn !== 'function') {
      //   return undefined;
      // }
      // return fn.apply(
      //   null,
      //   node.arguments.map(node =>
      //     evaluateExpression(node as CoreExpression, scope)
      //   )
      // );
    }
    // case 'ConditionalExpression':
    //   return legacyEvaluate(node.test as CoreExpression, context)
    //     ? legacyEvaluate(node.consequent as CoreExpression, context)
    //     : legacyEvaluate(node.alternate as CoreExpression, context);

    case 'Identifier': {
      return context.resolveIdentifier(node.name.toUpperCase());
      // const name = node.name.toUpperCase();
      // if (Object.hasOwn(standardProperties, name)) {
      //   return standardProperties[name].property_id;
      // }
      // if (Object.hasOwn(scope.functions, name)) {
      //   return (scope.functions as any)[name];
      // }
      // if (Object.hasOwn(scope.contextualIdentifiers, name)) {
      //   return (scope.contextualIdentifiers as any)[name];
      // }
      // if (Object.hasOwn(scope.identifier_values, name)) {
      //   return (scope.identifier_values as any)[name];
      // }
      // return null;
    }
    case 'Literal':
      return node.value;

    // case 'MemberExpression':
    //   return evaluateMember(node, context)[1];

    // case 'ThisExpression':
    //   return context;

    case 'UnaryExpression':
      return unops[node.operator](
        evaluateExpression(node.argument as CoreExpression, context)
      );

    default:
      throw new Error(`Unexpected node type "${(node as any).type}".`);
  }
}
